import {
    useCreatePortfolioActions,
    useCreatePortfolioSelectors,
} from 'datasource/CreatePortfolio';
import { usePortfolioProcessNavigation } from 'hooks/helpers/usePortfolioProcessNavigation';
import { useThreeBProductType } from 'hooks/isThreeBProduct';
import { useStrategySelection } from 'pages/ClientOverview/pages/Portfolios/hooks/useStrategySelection';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'ui-library';
import { calculateStrategyScore } from 'utils';
import { STRATEGY_CHANGED } from 'constants/sessionStorageKeys';
import { TPageError } from 'datasource/PortfolioProcessLayout/PortfolioFlowLayoutManager.types';
import useCommonAllocationDataProvider from 'pages/ClientOverview/pages/Portfolios/context/useCommonAllocationDataProvider';
import StrategySelection from './components/StrategySelection/StrategySelection';

const StrategySelectionPage = () => {
    const { t } = useTranslation();
    const [pageErrors, setPageErrors] = useState<TPageError | undefined>(undefined);
    const [continueInProgress, setContinueInProgress] = useState(false);

    const {
        selectedProductDescSelector,
        currentInvestmentApplicationSelector,
        is3aProductGroupSelector: is3aProduct,
        is3bProductGroupSelector: is3bProduct,
        isVbVbPremiumProductGroupSelector: isVbVbPremiumProduct,
        selectedProductSelector,
        currentInvestmentDescriptionSelector: cachedInvestmentDescription,
        currentRecommendedStrategySelector,
        advisoryDataSelector,
        isAdvisoryProcessSelector,
    } = useCreatePortfolioSelectors() as any;
    const { saveInvestmentApplicationDataAction } = useCreatePortfolioActions();

    const { setSetupWithdrawalPlan } = useCommonAllocationDataProvider();
    const {
        isZivZicAzp, isZiv, isZic, isAzp, isZivZifd, isZifd,
    } = useThreeBProductType({ product: selectedProductSelector });

    const recommendedStrategyScore = useMemo(() => {
        if (
            isAdvisoryProcessSelector
            && advisoryDataSelector?.metadata?.investorProfile?.score
        ) {
            return advisoryDataSelector?.metadata?.investorProfile?.score;
        }
        if (currentRecommendedStrategySelector?.ScoreRangePercentage) {
            return calculateStrategyScore(
                currentRecommendedStrategySelector?.ScoreRangePercentage,
            );
        }

        return currentRecommendedStrategySelector?.Score;
    }, [
        isAdvisoryProcessSelector,
        advisoryDataSelector?.metadata?.investorProfile?.score,
        currentRecommendedStrategySelector?.ScoreRangePercentage,
        currentRecommendedStrategySelector?.Score,
    ]);

    const {
        investmentHorizon,
        selectedStrategyId,
        recommendedStrategy,
        selectedProductMarkId,
        setIndividualPortfolioData,
        onInvestmentHorizonChange,
        onProductMarkClick,
        onSetupWithdrawalPlanChange,
        setupWithdrawalPlan,
        strategies,
        strategy,
        setSelectedStrategyId,
        isIndividualPortfolioSelected,
        individualPortfolioData,
        isRebalance,
        setRebalance,
        selectedProduct,
        isNavigationDisabled,
        products,
        isModalListLoading,
        isAllocationsLoading,
        showChart,
        modelPortfolioDetails,
        setIndividualPortfolioValidationError,
        investmentHorizonError,
        setInvestmentHorizonError,
        productMarksOptions,
        allocations,
        factsheetUrl,
        riskScore,
        checkTheRisk,
        productKey,
        preferenceStock,
        onPreferenceStockChange,
        preferenceExclusion,
        onPreferenceExclusionChange,
        preferenceInclusion,
        onPreferenceInclusionChange,
        exclusionSubstitute,
        onExclusionSubstituteChange,
        strategySelectedError,
        isZifdExclInterestIncome,
    } = useStrategySelection({
        productId: cachedInvestmentDescription?.productId,
        recommendedStrategyScore,
        cachedModelPortfolioId:
            cachedInvestmentDescription?.selectStrategy?.modelPortfolioId,
        cachedSetupWithdrawalPlan:
            cachedInvestmentDescription?.selectStrategy?.withdrawalOption,
        cachedPreferenceStock:
            currentInvestmentApplicationSelector?.preferenceStock,
        cachedPreferenceExclusion:
            currentInvestmentApplicationSelector?.preferenceExclusion,
        cachedPreferenceInclusion:
            currentInvestmentApplicationSelector?.preferenceInclusion,
        cachedExclusionSubstitute:
            currentInvestmentApplicationSelector?.exclusionSubstitute,
        cachedInvestmentHorizon:
            cachedInvestmentDescription?.selectStrategy?.investmentHorizon,
        setPageErrors,
        productSettings: selectedProductSelector?.settings,
        is3aProduct,
        is3bProduct,
        isVbVbPremiumProduct,
        isZiv,
        isZic,
        isAzp,
        isZifd,
    } as any);

    const handleOnContinue = useCallback(async () => {
        const strategyId = selectedStrategyId || recommendedStrategy?.Id;
        const tempSetupWithdrawalPlan = isAzp || setupWithdrawalPlan;
        const payload = {
            strategyId,
            recommendedStrategyId: recommendedStrategy?.Id,
            investmentHorizon: parseInt(investmentHorizon, 10),
            modelPortfolioId: selectedProductMarkId,
            withdrawalOption: is3bProduct ? tempSetupWithdrawalPlan : undefined,
            ...(isIndividualPortfolioSelected
                ? {
                    individualPortfolio: individualPortfolioData?.body?.reduce(
                        (accu: any[], item: any) => {
                            if (!item?.allocation?.disabled) {
                                accu.push({
                                    securityId: item?.allocation?.id,
                                    allocation: item?.allocation?.value / 100,
                                });
                            }

                            return accu;
                        },
                        [],
                    ),
                    rebalancing: isRebalance,
                }
                : {}),
        };

        const additionalData = {
            strategy,
            modelPortfolio: selectedProduct,
            riskScore,
            setupWithdrawalPlan: tempSetupWithdrawalPlan,
            preferenceStock,
            preferenceExclusion,
            exclusionSubstitute,
            preferenceInclusion,
        };

        await saveInvestmentApplicationDataAction({
            payload,
            method: 'saveStrategyData',
            additionalData,
        });

        if (is3bProduct) {
            setSetupWithdrawalPlan(tempSetupWithdrawalPlan);
        }

        stepComplete();
    }, [
        isRebalance,
        selectedProduct,
        individualPortfolioData,
        investmentHorizon,
        selectedStrategyId,
        recommendedStrategy?.Id,
        selectedProductMarkId,
        riskScore,
        setupWithdrawalPlan,
        preferenceStock,
        preferenceExclusion,
        exclusionSubstitute,
        preferenceInclusion,
        is3bProduct,
        isAzp,
        isIndividualPortfolioSelected,
        strategy,
        saveInvestmentApplicationDataAction,
        setSetupWithdrawalPlan,
    ]);

    const onContinue = useCallback(async () => {
        if (
            cachedInvestmentDescription?.selectStrategy?.modelPortfolioId
            !== selectedProductMarkId
        ) {
            sessionStorage.setItem(STRATEGY_CHANGED, 'true');
        }

        setContinueInProgress(true);
        if (!investmentHorizon) {
            setInvestmentHorizonError(t('validation.mandatoryField'));

            return;
        }

        const isTheRiskChanged = checkTheRisk(handleOnContinue);

        if (isTheRiskChanged) {
            Modal.confirm(isTheRiskChanged);

            return;
        }

        await handleOnContinue();
        setContinueInProgress(false);
    }, [
        investmentHorizon,
        checkTheRisk,
        handleOnContinue,
        cachedInvestmentDescription?.selectStrategy?.modelPortfolioId,
        selectedProductMarkId,
        setInvestmentHorizonError,
        t,
    ]);

    const { stepComplete } = usePortfolioProcessNavigation({
        saveOnContinue: onContinue,
        enableAutoStepComplete: false,
        continueInProgress,
        continueDisabled: continueInProgress,
        navigationDisabled: isNavigationDisabled,
        pageErrors,
    });

    return (
        <StrategySelection
            productDescription={selectedProductDescSelector}
            {...{
                investmentHorizon,
                investmentHorizonError,
                selectedProduct,
                recommendedStrategy,
                selectedStrategyId,
                setSelectedStrategyId,
                products,
                isIndividualPortfolioSelected,
                isModalListLoading,
                isAllocationsLoading,
                individualPortfolioData,
                isRebalance,
                setRebalance,
                modelPortfolioDetails,
                setIndividualPortfolioData,
                setIndividualPortfolioValidationError,
                selectedProductMarkId,
                productMarksOptions,
                showChart,
                allocations,
                factsheetUrl,
                onInvestmentHorizonChange,
                onProductMarkClick,
                strategies,
                cachedSelectStrategy:
                    cachedInvestmentDescription?.selectStrategy,
                productKey,
                onSetupWithdrawalPlanChange,
                setupWithdrawalPlan,
                is3bProduct,
                preferenceStock,
                onPreferenceStockChange,
                preferenceExclusion,
                onPreferenceExclusionChange,
                preferenceInclusion,
                onPreferenceInclusionChange,
                exclusionSubstitute,
                onExclusionSubstituteChange,
                isZivZicAzp,
                isZiv,
                isAzp,
                isZivZifd,
                strategySelectedError,
                isZic,
                isZifdExclInterestIncome,
            }}
        />
    );
};

export default StrategySelectionPage;
